import { useEffect, useState } from "react";
import SearchFilterInput from "../../form-fields/search-filter-input";
import SimpleButton from "../../buttons/Simple-button";
import H2 from "../../typography/H2";
import SortIcon from "../../../assets/svgs/SortIcon";
import "./top-listing-headers.css";
import SingleDropdown from "../../singleDropdown/SingleDropdown";
import Chevren_downIcon from "../../../assets/svgs/Chevren_down";
import { useDebounce } from "use-debounce";

interface ListingTopHeaderProps {
  PageHeading: string;
  className?: string;
  ButtonName?: string;
  onSearchTermChange: (term: string) => void;
  onClick?: () => void;
  handleSort: () => void;
  currentSortDirection: string;
  Count: number;
  handleSelectChange: (value: string, label: string) => void;
  filterOptions?: { title: string; count: number }[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
  aggregateValuesFilters?: Record<string, number>;
  selectedFilterLabel?: string;
  totalItemCount?: number;
  showAddButton?: boolean;
}

export default function TenantListingTopHeader({
  PageHeading,
  className = "",
  ButtonName,
  onSearchTermChange,
  onClick,
  handleSort,
  currentSortDirection,
  Count,
  handleSelectChange,
  activeTab,
  setActiveTab,
  filterOptions = [],
  aggregateValuesFilters,
  selectedFilterLabel = "All",
  totalItemCount,
  showAddButton = true,
}: ListingTopHeaderProps) {

  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(localSearchTerm, 500);

  useEffect(() => {
    onSearchTermChange(debouncedSearchTerm);
  }, [debouncedSearchTerm, onSearchTermChange]);

  const options = Object.entries(aggregateValuesFilters || {}).map(([title, count]) => ({ title, count }));
  const secondaryFilters = aggregateValuesFilters ? [
    { title: "All", count: totalItemCount ?? options.reduce((acc, curr) => acc + curr.count, 0) },
    ...options
  ] : [];

  // Reset search when tab changes
  useEffect(() => {
    setLocalSearchTerm("");
  }, [activeTab]);

  const totalCount = filterOptions.reduce((acc, option) => acc + option.count, 0);

  return (
    <div className={`top-listing-header ${className}`}>
      <div className="top-listing-header-top">
        <H2 text={`${PageHeading || ""}`} />
        {ButtonName && showAddButton && <SimpleButton label={ButtonName} onClick={onClick} />}
      </div>

      <div className="top-listing-header-filters">
        <div className="filter-options">
          {filterOptions.map((option, index) => (
            <button
              key={index}
              className={`filter-option${
                activeTab === option.title ? " filter-option-active" : ""
              }`}
              onClick={() => setActiveTab(option.title)}
            >
              {option.title} ({option.count})
            </button>
          ))}
        </div>

        <div className="search-sort-container">
          <SearchFilterInput
            placeholder="Search By Tenant Name"
            searchTerm={localSearchTerm}
            handleSearch={(e) => setLocalSearchTerm(e.target.value)}
          />

          <SingleDropdown
            key={`${activeTab}-${selectedFilterLabel}`} // Force re-render on tab/filter change
            triggerText={`${selectedFilterLabel || "All"} (${secondaryFilters.find(f => f.title === (selectedFilterLabel || "All"))?.count ?? ((selectedFilterLabel || "All") === "All" ? totalItemCount : 0)})`}
            triggerIcon={<Chevren_downIcon width={"18"} height={"18"} />}
            triggerIconPosition="right"
            menuItems={secondaryFilters.map(filter => ({
              label: `${filter.title} (${filter.count})`,
              action: () => handleSelectChange(filter.title, filter.title),
            }))}
          />

          <div className="sort-button" onClick={handleSort} role="button" tabIndex={0}>
            <span className="sort-text">
              {currentSortDirection === "asc"
                ? "Sort by: A-Z"
                : "Sort by: Z-A"}
            </span>
            <SortIcon />
          </div>
        </div>
      </div>
    </div>
  );
}